import { Link, useFetcher, useNavigate } from "@remix-run/react";
import { LoaderFunctionArgs, json } from "@vercel/remix";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
} from "~/components/ui/sheet";

import { useEffect, useState } from "react";

import { type Poster } from "~/types";

import { AspectRatio } from "~/components/ui/aspect-ratio";
import { AspectRatios } from "~/utils/constants";
import { Button } from "~/components/ui/button";
import { Image as CImage } from "~/components";
import { Input } from "~/components/ui/input";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import PopularSearches from "./PopularSearches";
import { motion } from "framer-motion";
import { useModalStore } from "~/utils";
import urlBuilder from "@sanity/image-url";
import { dataset, projectId } from "~/sanity/projectDetails";
import { loadQuery } from "~/sanity/loader.server";
import { loadQueryOptions } from "~/sanity/loadQueryOptions.server";
import {
  POSTERS_BY_SEARCH_QUERY,
  POSTERS_BY_SEARCH_TOTAL_COUNT_QUERY,
} from "~/sanity/queries";

const debounce = <F extends (...args: any[]) => any>(
  func: F,
  delay: number
): ((...args: Parameters<F>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<F>): void => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const query = url.searchParams.get("searchTerm");
  const locale = url.searchParams.get("locale");

  const { options } = await loadQueryOptions(request.headers, request.url);

  if (!query || query === "") {
    return json({ posters: [], postersTotalCount: 0 });
  }

  const postersInitial = await loadQuery<Poster[]>(
    POSTERS_BY_SEARCH_QUERY,
    {
      // @ts-ignore
      query: query.toLowerCase(),
      locale: locale,
    },
    options
  );

  const postersTotalCountInitial = await loadQuery<Poster[]>(
    POSTERS_BY_SEARCH_TOTAL_COUNT_QUERY,
    {
      // @ts-ignore
      query: query.toLowerCase(),
      locale: locale,
    },
    options
  );

  //const postersTotalCount = await getSearchedPostersTotalCount(query);

  return json({
    posters: postersInitial.data ?? undefined,
    postersTotalCount: postersTotalCountInitial.data ?? 0,
  });
};

export function SearchModal({ locale }: { locale: string }) {
  const modals = useModalStore((s) => s.modals);
  const closeModal = useModalStore((s) => s.closeModal);
  const openModal = useModalStore((s) => s.openModal);

  const [searchTerm, setSearchTerm] = useState("");

  let navigate = useNavigate();

  let search = useFetcher<any>();

  // bind command + k
  useEffect(() => {
    let listener = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        openModal("search");
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);

  // Debounce the search function
  const debouncedSearch = debounce((query: any) => {
    const formData = new FormData(query);
    formData.append("locale", locale);
    search.submit(formData, { method: "get", action: "/searchModal" });
  }, 600);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchTerm(query);
    debouncedSearch(event.currentTarget.form);
  };

  const handleOnSubmit = async (event: any) => {
    event.preventDefault();

    closeModal("search");
    navigate(`/${locale}/search?searchTerm=${searchTerm}`);
  };

  return (
    <>
      <Sheet
        open={!!modals["search"]}
        onOpenChange={() => closeModal("search")}
      >
        <SheetContent
          className="shadow-none flex items-center justify-center flex-col min-w-full md:min-w-[400px] p-8 mx-auto"
          side="top"
        >
          <div className="w-full max-w-[500px] py-4">
            <search.Form
              method="get"
              action="/searchModal"
              autoComplete="off"
              onSubmit={handleOnSubmit}
            >
              <div className="relative">
                <Input
                  className="pr-10 pl-3 h-12 "
                  placeholder={
                    locale === "se"
                      ? "Sök efter nyckelord, kategori..."
                      : "Search by keyword, category..."
                  }
                  type="search"
                  name="searchTerm"
                  //  defaultValue={search?.data?.query ?? ""}
                  value={searchTerm}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Escape" &&
                      event.currentTarget.value === ""
                    ) {
                      closeModal("search");
                    } else {
                      event.stopPropagation();
                    }
                  }}
                  onChange={handleInputChange}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <button type="submit">
                    <MagnifyingGlassIcon className="h-5 w-5 gray-400" />
                  </button>
                </div>
              </div>

              <div className=" p-0 pl-1 w-full inline-grid grid-flow-col  gap-2 grid-cols-[repeat(4,75px)] md:grid-cols-[repeat(4,116px)] overflow-hidden mt-10 mb-2">
                {search?.data?.posters && search?.data?.posters.length > 0
                  ? search?.data?.posters?.map((poster: Poster) => {
                      const imgSrcUrl = urlBuilder({ projectId, dataset })
                        .image(poster.displayImage)
                        .width(800)
                        .fit("max")
                        .auto("format")
                        .url();

                      function prefetchImage() {
                        let prefImage = new Image();
                        prefImage.src = imgSrcUrl;
                      }

                      return (
                        <SheetClose asChild key={poster.id}>
                          <Link
                            onMouseEnter={prefetchImage}
                            onFocus={prefetchImage}
                            className="grid gap-2 m-1 content-start cursor-pointer p-0 bg-transparent"
                            to={`/${locale}/posters/${poster.slug}`}
                          >
                            <motion.div
                              whileHover={{ scale: 1.03 }}
                              transition={{
                                duration: 0.5,
                                ease: [0.19, 1, 0.22, 1],
                              }}
                              className="relative h-full w-full cursor-pointer "
                            >
                              <AspectRatio ratio={AspectRatios.tall}>
                                <CImage
                                  imageSrcUrl={imgSrcUrl}
                                  alt={poster.displayImage.alt}
                                />
                              </AspectRatio>

                              <span className="font-thin text-xs line-clamp-2 line-h leading-2 mt-1">
                                {poster.name}
                              </span>
                            </motion.div>
                          </Link>
                        </SheetClose>
                      );
                    })
                  : null}
              </div>
              {searchTerm &&
              search?.data?.postersTotalCount === 0 &&
              search?.state !== "loading" ? (
                <span className="flex justify-center font-thin">
                  {locale === "se"
                    ? `Inga resultat för '${searchTerm}'`
                    : `No results for '${searchTerm}'`}
                </span>
              ) : null}

              {!search?.data ||
              (searchTerm === "" && search?.data?.postersTotalCount === 0) ? (
                <PopularSearches locale={locale} />
              ) : null}
            </search.Form>
          </div>
          {search?.data?.postersTotalCount &&
          search?.data?.postersTotalCount > 1 ? (
            <SheetFooter>
              <SheetClose asChild>
                <Button
                  asChild
                  size="lg"
                  className="w-[100%] md:w-64  m-[0.25rem] p-6"
                >
                  <Link
                    prefetch="intent"
                    className="cursor-pointer w-full"
                    to={`/${locale}/search?searchTerm=${searchTerm}`}
                  >
                    {`See all ${search.data.postersTotalCount} posters`}{" "}
                  </Link>
                </Button>
              </SheetClose>
            </SheetFooter>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
